/* ==========================================================================
   whero-current-flavors.js
   --------------------------------------------------------------------------
   Drives the /pages/current-flavors page:
     - reads the JSON bundle emitted by sections/whero-current-flavors.liquid
     - picks a default shop with this priority:
         1. localStorage saved slug (user's explicit pick)
         2. whero:status-updated event from whero-status-pill.js
            (benefits from the pill's cached position + IP-geo fallback,
             so users who haven't granted GPS still get the right shop)
         3. own navigator.geolocation.getCurrentPosition (slower fallback
            used only when the pill is absent or has no position data)
         4. defaultSlug (sanMarcos fallback)
     - remembers the pick in localStorage (key: wynstons.selectedShop)
     - wires the shop listbox (open/close, keyboard, selection) if the
       shop_bar block is present; still resolves a default shop either way
     - on every shop change:
         * updates the sticky bar shop name + Order CTA label/href
         * applies the three-state per-shop availability from the bundle:
             true  → normal card
             false → sold-out variant + notify-me form (on the menu at
                     this shop but 86'd — temporarily out)
             null  → card hidden entirely (flavor is not in this shop's
                     rotation at all)
         * picks a Last Call candidate — manual override (data-cf-lastcall-
           manual) wins; else auto-picks by urgency (is_last_tub first, then
           soonest rotation_end_date ≤ lastCallLeavingDays), filtered to
           flavors available at the selected shop
         * hides any category block marked data-cf-hide-when-empty="true"
           when zero cards remain visible at the selected shop
   All selectors scope to the [data-cf-root] set by the section so a second
   instance on another page can't collide.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'wynstons.selectedShop';

  var root = document.querySelector('[data-cf-root]');
  if (!root) return;

  var bundleEl = root.querySelector('script[data-cf-bundle]');
  if (!bundleEl) return;

  var bundle;
  try { bundle = JSON.parse(bundleEl.textContent); }
  catch (_) { return; }

  var shops = Array.isArray(bundle.shops) ? bundle.shops : [];
  var flavors = Array.isArray(bundle.flavors) ? bundle.flavors : [];

  var defaultSlug = bundle.defaultShopSlug || (shops[0] && shops[0].slug);
  var GEOFENCE_MI = typeof bundle.geofenceMiles === 'number' ? bundle.geofenceMiles : 60;
  var LAST_CALL_DAYS = typeof bundle.lastCallLeavingDays === 'number' ? bundle.lastCallLeavingDays : 7;

  /* ---- shop lookup ------------------------------------------------------ */
  var shopBySlug = {};
  shops.forEach(function (s) { shopBySlug[s.slug] = s; });

  /* ---- flavor → DOM pairing, per category list in render order ---------- */
  var flavorsByCategory = {};
  flavors.forEach(function (f) {
    if (!flavorsByCategory[f.category]) flavorsByCategory[f.category] = [];
    flavorsByCategory[f.category].push(f);
  });

  var flavorBySlug = {};
  flavors.forEach(function (f) { flavorBySlug[f.slug] = f; });

  function pairDomToFlavors(category) {
    var list = root.querySelector('[data-cf-list="' + category + '"]');
    if (!list) return [];
    var items = Array.prototype.slice.call(list.querySelectorAll(':scope > .whero-flavor-grid__item'));
    var pool = flavorsByCategory[category] || [];
    return items.map(function (li, i) {
      return { li: li, flavor: pool[i] || null };
    });
  }

  // Only pair categories that actually rendered a list.
  var categoryPairs = {};
  root.querySelectorAll('[data-cf-list]').forEach(function (list) {
    var cat = list.getAttribute('data-cf-list');
    if (cat) categoryPairs[cat] = pairDomToFlavors(cat);
  });

  /* ---- DOM refs (all optional — blocks can be absent) ------------------- */
  var shopBar      = root.querySelector('[data-cf-shop-bar]');
  var shopBtn      = root.querySelector('[data-cf-shop-button]');
  var shopMenu     = root.querySelector('[data-cf-shop-menu]');
  var shopNameEl   = root.querySelector('[data-cf-shop-name]');
  var shopOptions  = Array.prototype.slice.call(root.querySelectorAll('[data-cf-shop-option]'));
  var orderLink    = root.querySelector('[data-cf-order]');
  var orderLabel   = root.querySelector('[data-cf-order-label]');
  var lastCallEl          = root.querySelector('[data-cf-lastcall]');
  var lastCallEyebrow     = root.querySelector('[data-cf-lastcall-eyebrow]');
  var lastCallEyebrowText = root.querySelector('[data-cf-lastcall-eyebrow-text]');
  var lastCallName        = root.querySelector('[data-cf-lastcall-name]');
  var lastCallCopy        = root.querySelector('[data-cf-lastcall-copy]');
  var lastCallCta         = root.querySelector('[data-cf-lastcall-cta]');
  var lastCallImg         = root.querySelector('[data-cf-lastcall-img]');
  var lastCallManualSlug  = lastCallEl && lastCallEl.getAttribute('data-cf-lastcall-manual') || '';

  /* ---- persistence ------------------------------------------------------ */
  function readSavedSlug() {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch (_) { return ''; }
  }
  function writeSavedSlug(slug) {
    try { localStorage.setItem(STORAGE_KEY, slug); } catch (_) {}
  }

  /* ---- Haversine (miles) ------------------------------------------------ */
  function haversineMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.7613;
    var toRad = function (d) { return d * Math.PI / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function pickClosestShop(lat, lng) {
    var best = null;
    shops.forEach(function (s) {
      if (typeof s.lat !== 'number' || typeof s.lng !== 'number') return;
      var d = haversineMiles(lat, lng, s.lat, s.lng);
      if (!best || d < best.distance) best = { shop: s, distance: d };
    });
    return best;
  }

  /* ---- whero-status-pill bridge ---------------------------------------
     The status pill resolves position via cached pos → IP geo → GPS-on-
     click, then dispatches whero:status-updated with an `allShops` list
     (metaobject id + distanceMi per shop) and the closestShop. We match
     by metaobject id since the pill doesn't know our camelCase slugs. */

  function matchShopById(id) {
    if (id == null) return null;
    var idStr = String(id);
    for (var i = 0; i < shops.length; i++) {
      if (shops[i].id != null && String(shops[i].id) === idStr) return shops[i];
    }
    return null;
  }

  function enrichDistancesFromEvent(detail) {
    if (!detail || !Array.isArray(detail.allShops)) return;
    var bySlug = {};
    detail.allShops.forEach(function (entry) {
      if (!entry || !entry.shop || typeof entry.distanceMi !== 'number') return;
      var s = matchShopById(entry.shop.id);
      if (s) bySlug[s.slug] = entry.distanceMi;
    });
    shopOptions.forEach(function (btn) {
      var slug = btn.getAttribute('data-shop-slug');
      var d = bySlug[slug];
      if (typeof d !== 'number' || isNaN(d)) return;
      var distEl = btn.querySelector('[data-cf-shop-distance]');
      if (!distEl) return;
      distEl.textContent = d < 10 ? d.toFixed(1) + ' mi' : Math.round(d) + ' mi';
      distEl.hidden = false;
    });
  }

  function probeStatusPillState() {
    // The pill may have rendered (and dispatched its event) before this
    // script booted. Synchronously check its exposed state so we don't
    // wait 3s unnecessarily when a cached/resolved position is already
    // available.
    try {
      if (window.WheroStatusPill && typeof window.WheroStatusPill.getState === 'function') {
        var s = window.WheroStatusPill.getState();
        if (s && s.lastRender && s.lastRender.closest) {
          return {
            closestShop: s.lastRender.closest.shop,
            allShops: s.lastRender.ranked || [],
          };
        }
      }
    } catch (_) {}
    return null;
  }

  /* ---- default shop resolution ----------------------------------------
     cb receives (slug, userCoords|null). userCoords is {lat, lng} from
     navigator.geolocation when we got a fix — used downstream to enrich
     distance labels in the listbox (the event bridge enriches them on
     its own path, so userCoords is typically null when the event path
     resolves). */
  function resolveDefaultShop(cb) {
    var saved = readSavedSlug();
    if (!shops.length) { cb('', null); return; }

    // Always subscribe to future updates so the listbox re-enriches on
    // pill refresh (e.g. user clicks the pill to request GPS).
    document.addEventListener('whero:status-updated', function (e) {
      enrichDistancesFromEvent(e && e.detail);
    });

    // Saved pick wins — probe the pill synchronously for any already-
    // resolved state so we can enrich the listbox without waiting.
    if (saved && shopBySlug[saved]) {
      var probedSaved = probeStatusPillState();
      if (probedSaved) enrichDistancesFromEvent(probedSaved);
      cb(saved, null);
      return;
    }

    var settled = false;
    function finish(slug, coords) {
      if (settled) return;
      settled = true;
      cb(slug || defaultSlug, coords);
    }

    // Try the already-resolved pill state first (handles the race where
    // the pill dispatched before this listener attached).
    var probed = probeStatusPillState();
    if (probed) {
      var pickedSync = matchShopById(probed.closestShop && probed.closestShop.id);
      if (pickedSync) {
        enrichDistancesFromEvent(probed);
        finish(pickedSync.slug, null);
        return;
      }
    }

    // Then race: whero:status-updated vs. 3s timeout → own GPS fallback.
    function onStatusUpdate(e) {
      if (settled) return;
      enrichDistancesFromEvent(e && e.detail);
      var picked = matchShopById(e && e.detail && e.detail.closestShop && e.detail.closestShop.id);
      if (!picked) return;
      document.removeEventListener('whero:status-updated', onStatusUpdate);
      clearTimeout(eventTimer);
      finish(picked.slug, null);
    }
    document.addEventListener('whero:status-updated', onStatusUpdate);

    var eventTimer = setTimeout(function () {
      if (settled) return;
      document.removeEventListener('whero:status-updated', onStatusUpdate);
      fallbackGPS(finish);
    }, 3000);
  }

  function fallbackGPS(cb) {
    if (!navigator.geolocation) { cb(defaultSlug, null); return; }
    var done = false;
    var t = setTimeout(function () {
      if (done) return;
      done = true;
      cb(defaultSlug, null);
    }, 5000);
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        if (done) return;
        done = true;
        clearTimeout(t);
        var coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        var best = pickClosestShop(coords.lat, coords.lng);
        if (best && best.distance <= GEOFENCE_MI) cb(best.shop.slug, coords);
        else cb(defaultSlug, coords);
      },
      function () {
        if (done) return;
        done = true;
        clearTimeout(t);
        cb(defaultSlug, null);
      },
      { timeout: 4500, maximumAge: 10 * 60 * 1000 }
    );
  }

  /* ---- urgency classification ------------------------------------------ */
  function daysUntil(isoDate) {
    if (!isoDate) return null;
    var target = new Date(isoDate);
    if (isNaN(target.getTime())) return null;
    var now = new Date();
    return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  }

  /* Per-shop resolution: the shop-keyed fields (bridge-written from
     live inventory) win when they carry a value for this shop; the
     legacy global scalars remain the fallback. */
  function resolveLastTub(flavor, slug) {
    var by = flavor.isLastTubByShop;
    if (by && slug && Object.prototype.hasOwnProperty.call(by, slug)) {
      return by[slug] === true || by[slug] === 'true';
    }
    return !!flavor.isLastTub;
  }

  function resolveTubs(flavor, slug) {
    var by = flavor.tubsRemainingByShop;
    if (by && slug && by[slug] != null) return by[slug];
    return flavor.tubsRemaining;
  }

  function classifyUrgency(flavor, slug) {
    if (resolveLastTub(flavor, slug)) return { kind: 'last-tub' };
    var days = daysUntil(flavor.rotationEndDate);
    if (days !== null && days > 0 && days <= LAST_CALL_DAYS) {
      return { kind: 'leaving-soon', days: days };
    }
    return { kind: 'healthy' };
  }

  /* ---- apply state for a given shop ------------------------------------ */
  function applyShop(slug) {
    var s = shopBySlug[slug];

    // 1. Shop bar labels / order href
    if (s && shopNameEl) shopNameEl.textContent = s.name;
    if (s && orderLabel && orderLink) {
      // Normalize the prefix — merchants save it both with and without
      // a trailing space ("Order at" vs "Order at "), so always strip
      // and re-join with a single space to avoid "ORDER ATSAN MARCOS".
      var rawPrefix = orderLink.getAttribute('data-cf-order-prefix') || 'Order at';
      var prefix = String(rawPrefix).replace(/\s+$/, '');
      orderLabel.textContent = prefix + ' ' + s.name;
    }
    if (s && orderLink) orderLink.href = s.toastUrl;

    // 2. Listbox selection
    shopOptions.forEach(function (btn) {
      var isThis = btn.getAttribute('data-shop-slug') === slug;
      btn.setAttribute('aria-selected', isThis ? 'true' : 'false');
    });

    // 3. Cards — availability, sold-out, notify-me reveal
    Object.keys(categoryPairs).forEach(function (cat) {
      categoryPairs[cat].forEach(function (p) {
        if (!p.flavor) { p.li.hidden = false; return; }

        var avail = p.flavor.shopAvailability || {};
        var hasKey = Object.prototype.hasOwnProperty.call(avail, slug);
        // Three-state: true = available, false = 86'd (sold-out card),
        // null = not in this shop's rotation (hide). No slug / no key
        // degrades to available.
        var av = (!slug || !hasKey) ? true : avail[slug];

        var card = p.li.querySelector('.whero-flavor-card');
        if (!card) return;

        // Stamp the selected shop onto this card's notify-me form so a
        // restock signup here is tagged to the shop the visitor is
        // viewing. whero-notify-me.js reads data-shop at submit time.
        var notifyRoot = card.querySelector('[data-whero-notify]');
        if (notifyRoot) notifyRoot.setAttribute('data-shop', slug || '');

        if (av === true) {
          card.classList.remove('whero-flavor-card--sold-out');
          removeSoldOutPill(card);
          p.li.hidden = false;
        } else if (av === false) {
          card.classList.add('whero-flavor-card--sold-out');
          ensureSoldOutPill(card);
          p.li.hidden = false;
        } else {
          p.li.hidden = true;
        }
      });
    });

    // 4. Last Call pick
    renderLastCall(slug);

    // 5. Hide any category block with data-cf-hide-when-empty="true"
    //    when no cards are visible at this shop.
    root.querySelectorAll('[data-cf-hide-when-empty="true"]').forEach(function (section) {
      var listEl = section.querySelector('[data-cf-list]');
      var anyVisible = listEl
        ? Array.prototype.some.call(
            listEl.querySelectorAll(':scope > .whero-flavor-grid__item'),
            function (li) { return !li.hidden; }
          )
        : false;
      section.hidden = !anyVisible;
    });

    if (slug) writeSavedSlug(slug);

    // 6. Tell per-card urgency UI (whero-flavor-urgency.js) which shop
    //    the visitor is looking at, so pills/strips show that shop's own
    //    state instead of the cross-shop "most urgent" ranking.
    document.dispatchEvent(new CustomEvent('whero:shop-selected', {
      detail: { slug: slug || '' },
    }));
  }

  function ensureSoldOutPill(card) {
    var imgWrap = card.querySelector('.whero-flavor-card__img');
    if (!imgWrap) return;
    // Hide (don't remove) the urgency pill — it must survive switching
    // to a sold-out shop and back. Only our own sold-out pill is
    // recreated, so remove stale copies of that.
    Array.prototype.forEach.call(imgWrap.querySelectorAll('[data-cf-sold-out-pill]'), function (p) { p.remove(); });
    Array.prototype.forEach.call(imgWrap.querySelectorAll('.pill[data-flavor-urgency]'), function (p) { p.hidden = true; });
    var pill = document.createElement('span');
    pill.className = 'pill pill-sold-out';
    pill.setAttribute('data-cf-sold-out-pill', '');
    pill.textContent = 'Sold out';
    imgWrap.insertBefore(pill, imgWrap.firstChild);
    var strip = card.querySelector('.flavor-urgency');
    if (strip) strip.hidden = true;
  }

  function removeSoldOutPill(card) {
    var pill = card.querySelector('[data-cf-sold-out-pill]');
    if (pill) pill.remove();
    // Unhide urgency UI; the whero:shop-selected dispatch at the end of
    // applyShop re-renders (or re-hides) it for the selected shop.
    Array.prototype.forEach.call(card.querySelectorAll('.pill[data-flavor-urgency]'), function (p) { p.hidden = false; });
    var strip = card.querySelector('.flavor-urgency');
    if (strip) strip.hidden = false;
  }

  /* ---- Last Call -------------------------------------------------------
     Manual override (from the block setting) wins if present. Otherwise
     auto-pick the most urgent flavor available at the selected shop. */
  function renderLastCall(slug) {
    if (!lastCallEl) return;
    var shopObj = shopBySlug[slug];

    // Manual override mode
    if (lastCallManualSlug) {
      var manual = flavorBySlug[lastCallManualSlug];
      if (!manual) { lastCallEl.hidden = true; return; }
      // Respect shop availability on the manual pick too.
      if (shopObj) {
        var ma = manual.shopAvailability || {};
        var hasKey = Object.prototype.hasOwnProperty.call(ma, slug);
        var isAvail = !hasKey || ma[slug] === true;
        if (!isAvail) { lastCallEl.hidden = true; return; }
      }
      writeLastCallCard(manual, shopObj, classifyUrgency(manual, slug), slug);
      lastCallEl.hidden = false;
      return;
    }

    // Auto pick from urgency
    if (!shopObj) { lastCallEl.hidden = true; return; }

    var candidates = flavors.filter(function (f) {
      var avail = f.shopAvailability || {};
      var hasKey = Object.prototype.hasOwnProperty.call(avail, slug);
      var isAvail = !hasKey || avail[slug] === true;
      if (!isAvail) return false;
      var u = classifyUrgency(f, slug);
      return u.kind === 'last-tub' || u.kind === 'leaving-soon';
    });

    if (!candidates.length) { lastCallEl.hidden = true; return; }

    candidates.sort(function (a, b) {
      var ua = classifyUrgency(a, slug), ub = classifyUrgency(b, slug);
      if (ua.kind !== ub.kind) return ua.kind === 'last-tub' ? -1 : 1;
      if (ua.kind === 'leaving-soon') return (ua.days || 999) - (ub.days || 999);
      return 0;
    });

    var pick = candidates[0];
    writeLastCallCard(pick, shopObj, classifyUrgency(pick, slug), slug);
    lastCallEl.hidden = false;
  }

  function writeLastCallCard(flavor, shopObj, urgency, slug) {
    // Eyebrow pill — urgency-driven. Shows "Last tub" / "Leaving soon"
    // when the flavor qualifies; hidden entirely on the healthy state
    // so manual-pick features don't carry a stale scarcity cue.
    if (lastCallEyebrow) {
      if (urgency.kind === 'last-tub') {
        if (lastCallEyebrowText) lastCallEyebrowText.textContent = 'Last tub';
        lastCallEyebrow.hidden = false;
      } else if (urgency.kind === 'leaving-soon') {
        if (lastCallEyebrowText) lastCallEyebrowText.textContent = 'Leaving soon';
        lastCallEyebrow.hidden = false;
      } else {
        lastCallEyebrow.hidden = true;
      }
    }

    if (lastCallName) lastCallName.textContent = flavor.name || flavor.slug || '';

    if (lastCallImg) {
      if (flavor.imageUrl) {
        lastCallImg.src = flavor.imageUrl;
        lastCallImg.alt = (flavor.name || '') + ' ice cream';
      } else {
        lastCallImg.removeAttribute('src');
        lastCallImg.alt = '';
      }
    }

    if (lastCallCta && shopObj) lastCallCta.href = shopObj.toastUrl;

    if (lastCallCopy) {
      // textContent everywhere to keep merchant-sourced strings (shop
      // name, flavor description) out of the HTML parser.
      lastCallCopy.textContent = '';

      var strongText = '';
      var trailingText = '';
      var shopName = shopObj ? shopObj.name : '';

      if (urgency.kind === 'last-tub') {
        var tubsRaw = resolveTubs(flavor, slug);
        var tubs = (typeof tubsRaw === 'number' && tubsRaw > 0) ? tubsRaw : null;
        if (tubs && shopName) {
          strongText = 'Down to ' + tubs + ' ' + (tubs === 1 ? 'tub' : 'tubs') + ' at ' + shopName;
        } else if (shopName) {
          strongText = 'Last tub at ' + shopName;
        } else {
          strongText = 'Last tub';
        }
        trailingText = ' before this one rotates out.';
      } else if (urgency.kind === 'leaving-soon') {
        strongText = shopName
          ? 'Leaving in ~' + urgency.days + ' day' + (urgency.days === 1 ? '' : 's') + ' at ' + shopName + '.'
          : 'Leaving in ~' + urgency.days + ' day' + (urgency.days === 1 ? '' : 's') + '.';
      } else {
        // Manual-pick healthy state — just lead with the flavor's voice.
        strongText = 'Featured now';
        if (shopName) strongText += ' at ' + shopName;
        strongText += '.';
      }

      var strongEl = document.createElement('strong');
      strongEl.textContent = strongText;
      lastCallCopy.appendChild(strongEl);
      if (trailingText) lastCallCopy.appendChild(document.createTextNode(trailingText));
      if (flavor.description) {
        lastCallCopy.appendChild(document.createTextNode(' ' + flavor.description));
      }
    }
  }

  /* ---- enrich listbox rows with "{N} mi" once we know user coords ------ */
  function enrichDistances(userCoords) {
    if (!userCoords || typeof userCoords.lat !== 'number') return;
    shopOptions.forEach(function (btn) {
      var slug = btn.getAttribute('data-shop-slug');
      var sObj = shopBySlug[slug];
      if (!sObj || typeof sObj.lat !== 'number' || typeof sObj.lng !== 'number') return;
      var distEl = btn.querySelector('[data-cf-shop-distance]');
      if (!distEl) return;
      var d = haversineMiles(userCoords.lat, userCoords.lng, sObj.lat, sObj.lng);
      distEl.textContent = d < 10 ? d.toFixed(1) + ' mi' : Math.round(d) + ' mi';
      distEl.hidden = false;
    });
  }

  /* ---- listbox behavior ------------------------------------------------ */
  function openMenu() {
    if (!shopMenu || !shopBtn) return;
    shopMenu.hidden = false;
    shopBtn.setAttribute('aria-expanded', 'true');
    var firstOpt = shopOptions[0];
    if (firstOpt) firstOpt.focus();
  }
  function closeMenu() {
    if (!shopMenu || !shopBtn) return;
    shopMenu.hidden = true;
    shopBtn.setAttribute('aria-expanded', 'false');
  }
  function toggleMenu() {
    if (!shopMenu) return;
    if (shopMenu.hidden) openMenu(); else closeMenu();
  }

  if (shopBtn) {
    shopBtn.addEventListener('click', toggleMenu);
    shopBtn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
      }
    });
  }

  shopOptions.forEach(function (btn, idx) {
    btn.addEventListener('click', function () {
      var slug = btn.getAttribute('data-shop-slug');
      if (slug) applyShop(slug);
      closeMenu();
      if (shopBtn) shopBtn.focus();
    });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        var next = shopOptions[(idx + 1) % shopOptions.length];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = shopOptions[(idx - 1 + shopOptions.length) % shopOptions.length];
        if (prev) prev.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        if (shopOptions[0]) shopOptions[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        if (shopOptions[shopOptions.length - 1]) shopOptions[shopOptions.length - 1].focus();
      } else if (e.key === 'Escape') {
        closeMenu();
        if (shopBtn) shopBtn.focus();
      }
    });
  });

  document.addEventListener('click', function (e) {
    if (!shopMenu || shopMenu.hidden) return;
    var within = e.target.closest && e.target.closest('[data-cf-shop]');
    if (!within) closeMenu();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && shopMenu && !shopMenu.hidden) {
      closeMenu();
      if (shopBtn) shopBtn.focus();
    }
  });

  /* ---- boot ------------------------------------------------------------ */
  resolveDefaultShop(function (slug, userCoords) {
    if (slug) applyShop(slug);
    else if (lastCallEl && lastCallManualSlug) {
      // No shops but manual pick present — still render the band.
      renderLastCall('');
    }
    if (userCoords) enrichDistances(userCoords);
  });
})();
